import React, { useState, useEffect } from 'react';
import { ServiceProvider, Subscription, Location, City } from '@prisma/client';
import { useTranslation } from 'next-i18next';
import { useAuth } from '../../context/AuthUserContext';
import clsx from 'clsx';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Checkbox from '../atoms/Checkbox';
import Button from '../atoms/Button';
import Icon from '../atoms/Icon';
import Select from '../atoms/Select';
import Figure from '../molecules/Figure';
import Unfold from '../organisms/Unfold';
import Offset from '../organisms/Offset';
import BillingForm from '../organisms/BillingForm';
import { packages } from '../../data/packages';
import { useApp } from '../../context/AppContext';

import {
  Tier,
  fullPrices,
  discountFactor,
} from '../../constants/service-provider-prices';
import styles from '../../styles/components/organisms/ServiceProviderPackage.module.scss';
import axios from 'axios';

interface ServiceProviderPackagesProps {
  setLayer: (obj?: any) => void;
  serviceProvider?: ServiceProvider & {
    subscriptions: Subscription[];
    location: Location;
    cities: City[];
  };
  prices: Tier[];
}

export default function ServiceProviderPackages({
  serviceProvider,
  prices,
  setLayer,
}: ServiceProviderPackagesProps) {
  const { t } = useTranslation(['common', 'pages']);
  const { user } = useAuth() as any;
  const { devMode } = useApp();
  const { slug, ids, rights } = packages.service;
  const currPackages = ids.map((id: string, i: number) => {
    return {
      id,
      img: `/assets/thumbs/package-${id}.jpeg`,
      scopes: rights
        .filter((el: any) => !!el.scopes && el.scopes.indexOf(i) > -1)
        .map((el: any) => el.id),
      rights: rights
        .map((item: any) =>
          item.rights
            .filter((el: any) => !!el.scopes && el.scopes.indexOf(i) > -1)
            .map((el: any) => el.id)
        )
        .reduce((acc: any, curr: any) => acc.concat(curr), []),
    };
  });

  const packageOptions = ids.reduce(
    (acc: any, curr: string) =>
      acc.concat({
        value: curr,
        label: t(`common:${curr}`),
      }),
    [] as any
  );

  const inititalPackage = currPackages[2] || currPackages[0];
  const [currScope, setCurrScope] = useState(inititalPackage) as any;
  const [hoverScope, setHoverScope] = useState() as any;
  const [details, setDetails] = useState([]) as any;
  const [currency, setCurrency] = useState('usd');
  const [priceId, setPriceId] = useState('');
  const currTxt = hoverScope || currScope.id;
  const firstColW = 6 - ids.length;

  useEffect(() => {
    if (!!rights) {
      const opened = rights.map((el: any) => el.id);
      setDetails(opened);
    }
  }, [rights]);

  useEffect(() => {
    if (!!serviceProvider && !priceId) {
      // const priceID = serviceProvider.plan?.toLowerCase();
      const citiesCnt = serviceProvider.cities?.length || 0;
      const priceID =
        citiesCnt > 20
          ? 'thirtycities'
          : citiesCnt > 10
          ? 'twentycities'
          : citiesCnt > 5
          ? 'tencities'
          : citiesCnt > 1
          ? 'fivecities'
          : 'onecity';
      setPriceId(priceID);
    }
  }, [serviceProvider, priceId]);

  const setScope = (currScope: any) => {
    setCurrScope(currScope);
  };

  const selectScope = (e: any) => {
    const filteredScope = currPackages.filter(
      (el: any) => el.id === e.target.value
    )[0];
    setCurrScope(filteredScope);
  };

  const checkScope = (key: string, name: Tier) => {
    const filteredScope = currPackages.filter((el: any) => el.id === key)[0];
    setCurrScope(filteredScope);
    // setCurrPrice(name);
  };

  const toggleDetails = (id: string) => {
    const newDetails =
      details.indexOf(id) > -1
        ? details.filter((el: string) => el !== id)
        : [...details, id];
    setDetails(newDetails);
  };

  const subscribeByInvoice = async () => {
    if (!serviceProvider) {
      return;
    }

    const { billing_accounts } = user;

    setLayer(
      <BillingForm
        prefill={
          billing_accounts[0] || {
            billing_address: serviceProvider.location.address,
          }
        }
        account={billing_accounts}
        onCancel={() => setLayer()}
        onSubmit={(formData) => {
          window.location.href = `/api/venue/${serviceProvider.id}/upgrade?tier=${priceId}&currency=${currency}&account=${formData.account}&method=invoice`;
        }}
      />
    );
  };

  const subscribe = () => {
    axios.post(`/api/service-provider/${serviceProvider?.id}/track`, {
      event: 'publish',
    });

    if (!serviceProvider) {
      return;
    }
    const { billing_accounts } = user;

    setLayer(
      <BillingForm
        prefill={
          billing_accounts[0] || {
            billing_address: serviceProvider.location.address,
          }
        }
        account={billing_accounts}
        onCancel={() => setLayer()}
        onSubmit={proceed}
      />
    );
  };

  const proceed = (formData: any) => {
    if (!!serviceProvider) {
      window.location.href = `/api/service-provider/${serviceProvider.id}/upgrade?tier=${priceId}&currency=${currency}&billing_account_id=${user.billing_accounts[0].id}&account=${formData.account}`;
    }
  };

  const CustomCaption = (props: any) => {
    const id = props.id || currScope.id;
    const icon = `star-${id}`;
    const cnt = 3;
    return (
      <>
        <span>
          <Icon name={icon} />
          {cnt > 1 && <Icon name={icon} />}
          {cnt > 2 && <Icon name={icon} />}
        </span>
        <h2>{t(`common:${id}`)}</h2>
      </>
    );
  };

  const PricesRow = (selectable?: Boolean) => {
    if (!prices || !prices.length) {
      return '';
    }
    return (
      <>
        <Row>
          <Col xs={6} className={'offset-1'}>
            <div className={styles.prices}>
              {prices.map((price, i) => {
                const { name, type, prices } = price;
                const active = priceId === name;
                const checked = active;
                const currPrice =
                  (prices[currency]?.amount || 0) * discountFactor;
                const fullPrice = fullPrices[type][name][currency] || currPrice;
                const discount = currPrice < fullPrice;
                const sign = t(`common:${currency}-sign`);
                const colClass = clsx(
                  { [styles.active]: active },
                  { [styles.discount]: !!discount }
                );

                const PriceLabel = (
                  <>
                    {discount && (
                      <del>
                        {sign}
                        {fullPrice}
                        <br />
                      </del>
                    )}
                    <span>
                      {sign}
                      {currPrice}
                    </span>
                    <br />
                    <small>
                      {t(`common:per_year`)}
                      <sup>*</sup>
                    </small>
                  </>
                );

                return (
                  <div key={i} className={colClass}>
                    <h3>{t(`pages:${slug}:${name}`)}</h3>
                    {!!selectable ? (
                      <Checkbox
                        medium
                        dark
                        center
                        key={name}
                        name={name}
                        disabled={checked}
                        defaultValue={checked}
                        label={PriceLabel}
                        onChange={() => setPriceId(name)}
                      />
                    ) : (
                      <div>
                        <label>
                          {PriceLabel} <br />
                          <br />
                        </label>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Col>
          <Col />
          <Col />
          <Col xs={6} className={'offset-1'}>
            <Offset>
              <br />
              <br />
              <small>
                <sup>*</sup>
                {t(`pages:${slug}:price-note`)}
              </small>
            </Offset>
          </Col>
        </Row>
      </>
    );
  };

  const SubscribeRow = (position?: string) => {
    return (
      <Row>
        <Col xs={6} sm={4} md={2} className="offset-1 offset-sm-2 offset-md-3">
          {serviceProvider ? (
            <Offset>
              <Button
                full
                large
                dark
                icon="rocket"
                label={
                  currScope.id === 'basic'
                    ? t(`common:cta-publish-for-free`)
                    : t(`common:cta-upgrade`)
                }
                onClick={subscribe}
              />
              <br />
              <br />
              <div style={{ textAlign: 'center' }}>
                <a href="#" onClick={subscribeByInvoice}>
                  {t(`common:cta-pay-by-invoice`)}
                </a>
              </div>
              <br />
            </Offset>
          ) : (
            <Offset>
              <Button
                full
                large
                dark
                icon="rocket"
                href={'/promote-your-business/create/service'}
                label={t(`common:cta-create`)}
              />
              <br />
              <br />
            </Offset>
          )}
        </Col>
      </Row>
    );
  };

  return (
    <>
      <Container className={styles.serviceProviderPackage}>
        <Row>
          <Col xs={6} md={firstColW} className="d-md-block offset-1">
            <h1>{t(`pages:${slug}:h1`)}</h1>
            <h2>{t(`pages:${slug}:h2`)}</h2>
          </Col>
          <Col
            xs={6}
            md={4}
            className="offset-1 d-none offset-md-0 d-md-block"
          />
        </Row>

        {/* Phone start */}
        <Row className="d-md-none">
          <Col xs={6} sm={4} className="offset-1 offset-sm-2">
            <Figure
              round
              center
              img={currScope.img}
              customCaption={<CustomCaption />}
            />
          </Col>
          <Col xs={6} sm={4} className="offset-1 offset-sm-2">
            <br />
            {packageOptions.length > 1 && (
              <Select
                dark
                medium
                required
                name="scope-filter"
                options={packageOptions}
                onChange={selectScope}
                defaultValue={currScope.id}
              />
            )}
          </Col>
        </Row>
        {/* Phone end */}

        <Row>
          <Col xs={6} md={firstColW} className="d-md-block offset-1">
            <p>{t(`pages:${slug}:${currTxt}:desc`)}</p>
            <p>
              Join for a free trial in 2023 and benefit from 30% discount on the
              standard annual rate in 2024!{' '}
            </p>
          </Col>

          {currPackages.map((item: any, i: number) => (
            <Col key={i} xs={1} className="d-none d-md-flex">
              <Figure
                round
                center
                className={currScope.id === item.id ? styles.active : ''}
                img={item.img}
                onClick={() => setScope(item)}
                onMouseOver={() => {
                  setHoverScope(item.id);
                }}
                onMouseOut={() => setHoverScope(null)}
                customCaption={<CustomCaption id={item.id} />}
              />
            </Col>
          ))}
        </Row>

        <Row className="d-none d-lg-block">
          <Col />
        </Row>

        <Row>
          <Col xs={4} md={firstColW} className="d-md-block offset-1" />
          {currPackages.map((el: any, i: number) => {
            const active = currScope.id === el.id; // || hoverScope === el.id;
            const colClass = clsx(
              styles.roundTop,
              { [styles.active]: active },
              { 'd-none d-md-block': !active }
            );
            return <Col key={i} xs={2} md={1} className={colClass} />;
          })}
        </Row>

        {rights.map((item: any, index: number) => (
          <div key={index} className={styles.expandable}>
            <Row
              onClick={
                !!item.rights.length ? () => toggleDetails(item.id) : undefined
              }
            >
              <Col xs={4} md={firstColW} className="d-md-block offset-1">
                <div>
                  {!!item.rights.length && (
                    <Icon
                      name={
                        details.indexOf(item.id) > -1
                          ? 'arrow-up-fill'
                          : 'arrow-down-fill'
                      }
                    />
                  )}
                  <small>
                    <b>{t(`pages:${slug}:scope-${item.id}:label`)}</b>
                  </small>
                </div>
              </Col>
              {currPackages.map((el: any, i: number) => {
                const active = currScope.id === el.id; // || hoverScope === el.id;
                // const checks = (item.scopes || []).filter((id: any) =>
                //     el.scopes.indexOf(item.id) > -1
                //   ).length
                //   || item.rights.filter((item: any) =>
                //     el.rights.indexOf(item.id) > -1
                //   ).length;
                const checks =
                  (item.scopes || []).filter(
                    (id: any) => el.scopes.indexOf(item.id) > -1
                  ).length ||
                  item.rights.filter(
                    (item: any) => el.rights.indexOf(item.id) > -1
                  ).length;

                const iconName = !!checks ? 'check-big' : 'dash';
                const colClass = clsx(
                  { [styles.active]: active },
                  { 'd-none d-md-block': !active },
                  { [styles.grey]: checks < item.rights.length }
                );
                return (
                  <Col key={i} xs={2} md={1} className={colClass}>
                    <div>
                      <Icon name={iconName} />
                    </div>
                  </Col>
                );
              })}
            </Row>

            <Unfold
              open={details.indexOf(item.id) > -1}
              className={styles.unfold}
            >
              {item.rights.map((right: any, i: number) => {
                return (
                  <Row key={i}>
                    <Col xs={4} md={firstColW} className="d-md-block offset-1">
                      <div>
                        <small>
                          {t(`pages:${slug}:scope-${item.id}:scope-${i + 1}`)}
                        </small>
                      </div>
                    </Col>
                    {currPackages.map((el: any, i: number) => {
                      const active = currScope.id === el.id; // || hoverScope === el.id;
                      const checks = el.rights.indexOf(right.id) > -1;
                      const iconName = checks ? 'check' : 'dash';
                      const colClass = clsx(
                        { [styles.active]: active },
                        { ['d-none d-md-block']: currScope.id !== el.id }
                      );
                      return (
                        <Col key={i} xs={2} md={1} className={colClass}>
                          <div>
                            <Icon name={iconName} />
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                );
              })}
            </Unfold>
          </div>
        ))}

        <Row>
          <Col xs={4} md={firstColW} className="offset-1" />
          {currPackages.map((el: any, i: number) => {
            const active = currScope.id === el.id;
            const colClass = clsx(
              styles.roundBottom,
              { [styles.active]: active },
              { 'd-none d-md-block': !active }
            );
            return <Col key={i} xs={2} md={1} className={colClass} />;
          })}
        </Row>
        <br />
        <br />
        <Row className={styles.spacerRow}>
          <Col xs={6} md={4} lg={5} className="offset-1">
            <div>
              <p>{t(`pages:${slug}:${currTxt}:info`)}</p>
            </div>
          </Col>
          <Col xs={6} md={2} lg={1} className="offset-1 offset-md-0">
            <Offset>
              <Select
                defaultValue={currency}
                onChange={(e) => setCurrency(e.target.value)}
                options={[
                  { value: 'usd', label: 'USD' },
                  { value: 'eur', label: 'EUR' },
                  { value: 'cny', label: 'CNY' },
                ]}
              />
            </Offset>
          </Col>
        </Row>

        {PricesRow()}

        {SubscribeRow('bottom')}
      </Container>
    </>
  );
}
